import {
  getComponentRule,
  siteSchema,
  COMPONENT_TYPES,
  isComponentType,
} from "@zirro/schema";
import type { RegistryEntry } from "./types";
import { INSPECTOR_FIELDS } from "./inspector";
import {
  Section,
  Container,
  Heading,
  Text,
  Image,
  Button,
  Header,
  Footer,
  Card,
  FeatureGrid,
  Badge,
  Divider,
  Spacer,
} from "./components";

const COMPONENTS = {
  Section,
  Container,
  Heading,
  Text,
  Image,
  Button,
  Header,
  Footer,
  Card,
  FeatureGrid,
  Badge,
  Divider,
  Spacer,
} as const;

/** $defs key for a component's props body, e.g. Heading -> headingPropsBody. */
function propsSchemaFor(type: string): Record<string, unknown> {
  const defs = (siteSchema as { $defs?: Record<string, unknown> }).$defs ?? {};
  const target = `${type.toLowerCase()}propsbody`;
  for (const [k, v] of Object.entries(defs)) {
    if (k.toLowerCase() === target) {
      const s = v as Record<string, unknown>;
      return { properties: {}, ...s };
    }
  }
  const fields = INSPECTOR_FIELDS[type] ?? [];
  const properties: Record<string, unknown> = {};
  for (const f of fields) {
    properties[f.prop] = { type: f.control === "number" ? "number" : "string" };
  }
  return { type: "object", properties };
}

/**
 * Single source of truth for what the builder can render (PRD §81–83, §126).
 * Editor palette, validation, inspector, and renderer all read this registry.
 */
const registry = new Map<string, RegistryEntry>(
  COMPONENT_TYPES.map((type) => {
    const rule = getComponentRule(type)!;
    return [
      type,
      {
        type,
        label: rule.label,
        isContainer: rule.isContainer,
        allowedParents: [...rule.allowedParents],
        defaultProps: structuredClone(rule.defaultProps),
        propsSchema: propsSchemaFor(type),
        inspector: INSPECTOR_FIELDS[type],
        Component: COMPONENTS[type],
      },
    ];
  }),
);

/** Returns a defensive copy so callers can never pollute the registry. */
function detach(entry: RegistryEntry): RegistryEntry {
  return { ...entry, defaultProps: structuredClone(entry.defaultProps) };
}

export function getRegistryEntry(type: string): RegistryEntry | undefined {
  const entry = registry.get(type);
  return entry ? detach(entry) : undefined;
}

export function listRegistry(): RegistryEntry[] {
  return [...registry.values()].map(detach);
}

export function isRenderable(type: string): boolean {
  return isComponentType(type) && registry.has(type);
}
